export type SortOrder = 'asc' | 'desc';

export type EquipmentListSortField = 'displayId' | 'health' | 'importanceLevel';
export type DeteriorationListSortField = 'equipmentName' | 'health' | 'importanceLevel';
export type PartListSortField = 'displayId' | 'name' | 'stockStatus';
export type SupplierListSortField = 'name' | 'contactPerson' | 'updatedAt';
export type DocumentListSortField = 'title' | 'createdAt' | 'association';

export type SortPreference<TField extends string> = {
  field: TField;
  order: SortOrder;
};

export const EQUIPMENT_LIST_SORT_STORAGE_KEY = 'fc-equipment-list-sort-preference-v1';
export const DETERIORATION_LIST_SORT_STORAGE_KEY = 'fc-deterioration-list-sort-preference-v1';
export const PART_LIST_SORT_STORAGE_KEY = 'fc-part-list-sort-preference-v1';
export const SUPPLIER_LIST_SORT_STORAGE_KEY = 'fc-supplier-list-sort-preference-v1';
export const DOCUMENT_LIST_SORT_STORAGE_KEY = 'fc-document-list-sort-preference-v1';

export const DEFAULT_EQUIPMENT_LIST_SORT: SortPreference<EquipmentListSortField> = {
  field: 'displayId',
  order: 'asc',
};

export const DEFAULT_DETERIORATION_LIST_SORT: SortPreference<DeteriorationListSortField> = {
  field: 'health',
  order: 'asc',
};

export const DEFAULT_PART_LIST_SORT: SortPreference<PartListSortField> = {
  field: 'displayId',
  order: 'asc',
};

export const DEFAULT_SUPPLIER_LIST_SORT: SortPreference<SupplierListSortField> = {
  field: 'name',
  order: 'asc',
};

export const DEFAULT_DOCUMENT_LIST_SORT: SortPreference<DocumentListSortField> = {
  field: 'createdAt',
  order: 'desc',
};

export const loadSortPreference = <TField extends string>(
  storageKey: string,
  validFields: readonly TField[],
  fallback: SortPreference<TField>,
): SortPreference<TField> => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw) as Partial<SortPreference<TField>>;
    if (!parsed || typeof parsed !== 'object') {
      return fallback;
    }

    const field = parsed.field;
    const order = parsed.order;
    if (!field || !validFields.includes(field) || (order !== 'asc' && order !== 'desc')) {
      return fallback;
    }

    return {
      field,
      order,
    };
  } catch {
    return fallback;
  }
};

export const persistSortPreference = <TField extends string>(
  storageKey: string,
  preference: SortPreference<TField>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preference));
  } catch {
    // Ignore persistence failures and continue with in-memory state.
  }
};

export const getSortOrderLabel = (field: string, order: SortOrder): string => {
  if (field === 'health' || field === 'importanceLevel') {
    return order === 'asc' ? '低い順' : '高い順';
  }
  if (field === 'stockStatus') {
    return order === 'asc' ? '不足順' : '充足順';
  }
  if (field === 'createdAt' || field === 'updatedAt') {
    return order === 'asc' ? '古い順' : '新しい順';
  }
  return order === 'asc' ? '昇順' : '降順';
};
